import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { breadcrumbsFor } from "@/lib/seoLinks";

/**
 * Breadcrumbs — visible, accessible, SEO-friendly navigation that mirrors
 * the site's real hierarchy. Renders:
 *   1. Linked HTML <a> anchors (via react-router <Link>) so Google can
 *      crawl them from the initial HTML.
 *   2. BreadcrumbList JSON-LD so Google can render rich-result trails in SERP.
 *
 * Behavior:
 *   - Resolves the trail from `useLocation()` by default; override with `trail`.
 *   - Hidden on Home (`/`) — breadcrumbs there would be noise.
 *   - Omits the final crumb's href so it renders as plain text ("current page").
 *   - Links use the destination page's descriptive title, NOT "page", "here", etc.
 */
export default function Breadcrumbs({ trail, className = "" }) {
  const loc = useLocation();
  const items = trail || breadcrumbsFor(loc.pathname);

  // JSON-LD payload — one list item per crumb with a 1-based position.
  const jsonLd = useMemo(() => {
    if (!items || items.length === 0) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://dividendportfolioplanner.com";
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.label,
        ...(c.href ? { item: origin + c.href } : {}),
      })),
    };
  }, [items]);

  if (!items || items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className={`mb-4 ${className}`}
      data-testid="breadcrumbs"
    >
      <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3 opacity-50" aria-hidden="true" />}
              {c.href && !isLast ? (
                <Link
                  to={c.href}
                  className="hover:text-primary transition-colors inline-flex items-center gap-1"
                  data-testid={`breadcrumb-link-${i}`}
                >
                  {i === 0 && <Home className="w-3 h-3" aria-hidden="true" />}
                  {c.label}
                </Link>
              ) : (
                <span
                  className={`inline-flex items-center gap-1 ${isLast ? "text-foreground" : ""}`}
                  aria-current={isLast ? "page" : undefined}
                  data-testid={`breadcrumb-current`}
                >
                  {i === 0 && <Home className="w-3 h-3" aria-hidden="true" />}
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </nav>
  );
}
